import { Children, createContext, isValidElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiDownload, FiUpload } from "react-icons/fi";
import { downloadCsv } from "../../utils/exportCsv";
import { downloadXlsx } from "../../utils/exportExcel";
import EmptyState from "./EmptyState";

// [NEW] Every table lists its rows A to Z (ignoring case and accents; numbers compare as numbers, so "E2" comes before "E10").
// - By default the rows are sorted by the first column that has text (usually the employee / item name).
// - <DataTable sortBy="employeeName"> (a column key) or sortBy={(row) => ...} picks another sort value.
// - A column can define sortValue={(row) => ...}.
// - <DataTable keepOrder> turns it off where the order has a meaning (newest first, row numbers, rating bands, audit trail).
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

// Visible text of a rendered cell, e.g. <PersonCell name="Asha" meta="E1001" /> -> "Asha"
function textOf(node) {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(" ");
  if (isValidElement(node)) {
    const { children, name, label, title, status } = node.props;
    if (children !== undefined && Children.count(children) > 0) return textOf(children);
    return [name, label, title, status].find((v) => typeof v === "string") || "";
  }
  return "";
}

function cellText(column, row) {
  if (column.sortValue) return String(column.sortValue(row) ?? "");
  const raw = row[column.key];
  if (typeof raw === "string" || typeof raw === "number") return String(raw);
  return column.render ? textOf(column.render(row)).trim() : "";
}

// [NEW] Export / import on every table.
// - Every table with more than one row shows "Export" (Excel or CSV). It exports ALL rows the filters leave (every
//   page), with the visible column titles. Action columns (no title) are skipped.
//   A column can give exportValue={(row) => ...} when its cell is not plain text. exportable={false} hides the button.
// - A page that can create these records passes onImport={() => ...}; the table then shows "Import" next to Export.
function exportText(column, row) {
  if (column.exportValue) return column.exportValue(row);
  if (typeof column.value === "function") return column.value(row);
  const raw = row[column.key];
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && !column.render) return raw;
  if (column.render) {
    const text = textOf(column.render(row)).replace(/\s+/g, " ").trim();
    if (text) return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : text;
  }
  if (typeof raw === "string") return raw;
  return column.sortValue ? String(column.sortValue(row) ?? "") : "";
}

function fileBase(name) {
  const page = typeof document !== "undefined" ? document.querySelector(".page h1, h1")?.textContent : "";
  const base = String(name || page || "export").trim().replace(/[^A-Za-z0-9 _-]+/g, "").replace(/\s+/g, "_") || "export";
  return `${base}_${new Date().toISOString().slice(0, 10)}`;
}

// [NEW] Pages wrapped in <NoTableTools> (the Admin menu pages) show no Export / Import on any of their tables,
// including tables inside their dialogs.
const TableToolsContext = createContext(true);
export function NoTableTools({ children }) {
  return <TableToolsContext.Provider value={false}>{children}</TableToolsContext.Provider>;
}

function TableToolbar({ columns, rows, name, exportable, onImport, importLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const showExport = exportable && rows.length > 1;
  if (!showExport && !onImport) return null;
  const cols = columns.filter((c) => c.label && c.export !== false);
  const run = (kind) => {
    setOpen(false);
    const header = cols.map((c) => (typeof c.label === "string" ? c.label : textOf(c.label) || c.key));
    const out = cols.map((c, i) => ({ key: `c${i}`, label: header[i], value: (r) => exportText(c, r) }));
    if (kind === "xlsx") downloadXlsx(fileBase(name), [{ name: String(name || "Data").slice(0, 31), columns: out, rows }]);
    else downloadCsv(fileBase(name), out, rows);
  };
  return (
    <div className="table-toolbar">
      {onImport && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onImport}><FiUpload size={14} aria-hidden="true" /> {importLabel || "Import"}</button>
      )}
      {showExport && (
        <div className="table-export" ref={ref}>
          <button type="button" className="btn btn-ghost btn-sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            <FiDownload size={14} aria-hidden="true" /> Export
          </button>
          {open && (
            <div className="table-export-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => run("xlsx")}>Excel (.xlsx), {rows.length} rows</button>
              <button type="button" role="menuitem" onClick={() => run("csv")}>CSV (.csv), {rows.length} rows</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DataTable({ columns, rows: inputRows = [], rowKey = "id", onRowClick, emptyTitle = "Nothing here yet", emptyText, pageSize = 10, loading = false, dense = false, label, selection, sortBy, keepOrder = false, exportable = true, exportName, onImport, importLabel }) {
  const [page, setPage] = useState(1);
  const toolsAllowed = useContext(TableToolsContext);
  // [NEW] alphabetical order (before paging, so page 1 holds the A's of the whole list)
  const sortedRows = useMemo(() => {
    if (keepOrder || inputRows.length < 2) return inputRows;
    let valueOf;
    if (typeof sortBy === "function") valueOf = (r) => String(sortBy(r) ?? "");
    else {
      const col = sortBy
        ? columns.find((c) => c.key === sortBy)
        : columns.find((c) => inputRows.some((r) => cellText(c, r).trim() !== ""));
      if (!col) return inputRows;
      valueOf = (r) => cellText(col, r);
    }
    const keyed = inputRows.map((r) => [valueOf(r).trim(), r]);
    // rows without a value go last; ties keep their original order
    return keyed.sort((a, b) => (a[0] === "" ? 1 : 0) - (b[0] === "" ? 1 : 0) || collator.compare(a[0], b[0])).map(([, r]) => r);
  }, [inputRows, columns, sortBy, keepOrder]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = sortedRows;
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pages);
  const visible = useMemo(() => rows.slice((current - 1) * pageSize, current * pageSize), [rows, current, pageSize]);
  const keyOf = (row, i) => (typeof rowKey === "function" ? rowKey(row) : row[rowKey]) ?? i;

  // [NEW] Optional row selection: selection = { selected: Set, onChange(Set), isSelectable(row), label }.
  // The header checkbox selects every selectable row in `rows` (all pages, i.e. everything the filters leave).
  const selectable = useMemo(
    () => (selection ? rows.filter((r) => (selection.isSelectable ? selection.isSelectable(r) : true)) : []),
    [rows, selection] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const selectedCount = selection ? selectable.filter((r) => selection.selected.has(keyOf(r))).length : 0;
  const allSelected = selectable.length > 0 && selectedCount === selectable.length;
  const headRef = useRef(null);
  useEffect(() => { if (headRef.current) headRef.current.indeterminate = selectedCount > 0 && !allSelected; }, [selectedCount, allSelected]);
  const toggleAll = () => {
    const next = new Set(selection.selected);
    selectable.forEach((r) => { if (allSelected) next.delete(keyOf(r)); else next.add(keyOf(r)); });
    selection.onChange(next);
  };
  const toggleOne = (row) => {
    const next = new Set(selection.selected);
    const k = keyOf(row);
    if (next.has(k)) next.delete(k); else next.add(k);
    selection.onChange(next);
  };

  return (
    <div className="table-card">
      {!loading && toolsAllowed && <TableToolbar columns={columns} rows={rows} name={exportName || label} exportable={exportable} onImport={onImport} importLabel={importLabel} />}
      <div className="table-wrap" tabIndex={0} {...(label ? { role: "region", "aria-label": `${label} (scrollable table)` } : {})}>
        <table className={`data-table ${dense ? "is-dense" : ""}`}>
          <thead>
            <tr>
              {selection && (
                <th scope="col" className="col-select">
                  <input
                    ref={headRef}
                    type="checkbox"
                    checked={allSelected}
                    disabled={selectable.length === 0}
                    onChange={toggleAll}
                    aria-label={`Select all ${selectable.length} ${selection.label || "rows"}`}
                    title={`Select all ${selectable.length} ${selection.label || "rows"}`}
                  />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width, textAlign: c.align || "left" }} scope="col">
                  {c.label || <span className="sr-only">Actions</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="skeleton-row">
                  {selection && <td className="col-select" />}
                  {columns.map((c) => <td key={c.key}><span className="skeleton-line" /></td>)}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selection ? 1 : 0)}>
                  <EmptyState title={emptyTitle} text={emptyText} compact />
                </td>
              </tr>
            ) : (
              visible.map((row, i) => (
                <tr
                  key={keyOf(row, i)}
                  className={onRowClick ? "is-clickable" : ""}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === "Enter") onRowClick(row); } : undefined}
                >
                  {selection && (
                    <td className="col-select" onClick={(e) => e.stopPropagation()}>
                      {(selection.isSelectable ? selection.isSelectable(row) : true) && (
                        <input
                          type="checkbox"
                          checked={selection.selected.has(keyOf(row, i))}
                          onChange={() => toggleOne(row)}
                          aria-label={`Select ${row.name || keyOf(row, i)}`}
                        />
                      )}
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || "left" }}>
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > pageSize && (
        <div className="table-footer">
          <span>
            Showing {(current - 1) * pageSize + 1} to {Math.min(current * pageSize, rows.length)} of {rows.length}
          </span>
          <div className="pager">
            <button type="button" className="icon-btn" disabled={current === 1} onClick={() => setPage(current - 1)} aria-label="Previous page">
              <FiChevronLeft size={16} />
            </button>
            <span>{current} / {pages}</span>
            <button type="button" className="icon-btn" disabled={current === pages} onClick={() => setPage(current + 1)} aria-label="Next page">
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
